import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { notification } from 'tea-component';
import { getStudioChartEvents } from '@sg/services/index';
import { shouldDisableEventToggle } from './eventSettingUtils';

type TEventRequestType = 'overview' | 'studios' | 'publishing' | 'business';

interface IUseEventSettingParams {
  revenueData: any[];
  chartRef: React.MutableRefObject<any>;
  showChatsTypeValue?: string;
  activeCard: string;
  eventType: TEventRequestType;
  studioId?: string;
}

const useEventSetting = ({
  revenueData,
  chartRef,
  showChatsTypeValue,
  activeCard,
  eventType,
  studioId,
}: IUseEventSettingParams) => {
  const [chartEventSetting, setChartEventSetting] = useState({
    showEventBubbles: true,
    bubbleEvent: {},
  });
  const [eventSettingVisible, setEventSettingVisible] = useState(false);
  const [isChartReady, setIsChartReady] = useState(false);
  // Event data state - fetched from API
  const [eventApiData, setEventApiData] = useState<any>(null);
  // Track last fetched key to prevent duplicate calls.
  const lastFetchedKeyRef = useRef<string | null>(null);
  // Track in-flight key to avoid duplicate concurrent requests.
  const inFlightFetchKeyRef = useRef<string | null>(null);
  // Ignore stale responses when key changes quickly (e.g. switching studios).
  const latestRequestSeqRef = useRef(0);

  const fetchKey = useMemo(() => {
    const months = (Array.isArray(revenueData) ? revenueData : [])
      .map((item: any) => item?.month)
      .filter(Boolean);
    const startDate = months[0] || '';
    const endDate = months[months.length - 1] || '';
    const monthsSignature = months.join('|');

    return `${eventType}:${eventType === 'studios' ? (studioId || '') : 'all'}:${startDate}:${endDate}:${monthsSignature}`;
  }, [eventType, studioId, revenueData]);

  // Event data for EventSetting - backend already groups by game_title
  // Structure: { "Warframe": [events...], "Soulframe": [events...], ... }
  // Each event has: id, game_title, date, event_content, is_checked, cover, studio_name
  const eventDataByGame = useMemo(
    () => eventApiData?.all_events || {},
    [eventApiData],
  );
  const showEventsDisabled = useMemo(
    () => shouldDisableEventToggle(eventApiData),
    [eventApiData],
  );

  // Fetch studio chart events from API
  const fetchStudioChartEvents = useCallback(async () => {
    if (!revenueData || revenueData.length === 0) return;
    if (eventType === 'studios' && !studioId) return;

    // Skip if already fetched.
    if (lastFetchedKeyRef.current === fetchKey) return;
    // Skip if same request is already in-flight.
    if (inFlightFetchKeyRef.current === fetchKey) return;

    // Get date range from revenueData
    const months = revenueData.map((item: any) => item?.month).filter(Boolean);
    if (months.length === 0) return;

    const startDate = months[0]; // First month (e.g., "2025-01")
    const endDate = months[months.length - 1]; // Last month (e.g., "2025-12")

    // Clear stale overlay only when a real new request is about to start.
    // This avoids blanking events on activeCard switches where fetch is skipped
    // because the key is unchanged.
    setIsChartReady(false);
    setEventApiData(null);
    setChartEventSetting((prev) => ({
      ...prev,
      bubbleEvent: {},
    }));

    const requestSeq = latestRequestSeqRef.current + 1;
    latestRequestSeqRef.current = requestSeq;
    inFlightFetchKeyRef.current = fetchKey;

    try {
      const response = await getStudioChartEvents({
        type: eventType as any,
        ...(eventType === 'studios' && studioId
          ? { studio_id: studioId }
          : {}),
        start_date: startDate,
        end_date: endDate,
      });

      if (response?.data) {
        let responseData = response.data;

        // Transform event_timeline array to object map (if it is array)
        if (
          responseData.event_timeline
          && Array.isArray(responseData.event_timeline)
        ) {
          const timelineObj: any = {};
          responseData.event_timeline.forEach((item: any) => {
            if (item.month) {
              const eventList: any = {};
              // Convert projects array to event_list map
              if (Array.isArray(item.projects)) {
                item.projects.forEach((proj: any) => {
                  if (proj.game_title) {
                    eventList[proj.game_title] = proj.events;
                  }
                });
              }
              timelineObj[item.month] = {
                ...item,
                event_list: eventList,
              };
            }
          });
          responseData = {
            ...responseData,
            event_timeline: timelineObj,
          };
        }

        // Transform all_events array to object map (if it is array)
        if (responseData.all_events && Array.isArray(responseData.all_events)) {
          const allEventsObj: any = {};
          responseData.all_events.forEach((item: any) => {
            if (item.game_title) {
              allEventsObj[item.game_title] = item.events;
            }
          });
          responseData = {
            ...responseData,
            all_events: allEventsObj,
          };
        }

        // Only latest request updates state.
        if (requestSeq === latestRequestSeqRef.current) {
          setEventApiData(responseData);
          lastFetchedKeyRef.current = fetchKey;
        }
      }
    } catch (error) {
      console.error('Error fetching studio chart events:', error);
      notification.error({
        description: 'Failed to load Events',
      });
    } finally {
      if (inFlightFetchKeyRef.current === fetchKey) {
        inFlightFetchKeyRef.current = null;
      }
    }
  }, [eventType, fetchKey, revenueData, studioId]);

  // Fetch events when revenueData changes
  useEffect(() => {
    fetchStudioChartEvents();
  }, [fetchStudioChartEvents]);

  // Initialize event data when event API data changes
  useEffect(() => {
    if (!eventApiData?.event_timeline) return;

    // Hide events immediately when data changes (same pattern as window resize)
    // This prevents overlapping events during the transition
    setIsChartReady(false);

    // Transform API data to EventWindows format: { "2026-01": [events...], "2026-02": [events...] }
    // Backend groups events by project (game_title). Events with same game_title share cover, studio_name, date
    // but have different event_content. We consolidate them into one item with event_contents array.
    const timelineEvents: any = {};
    Object.keys(eventApiData.event_timeline).forEach((month: string) => {
      const monthData = eventApiData.event_timeline[month];
      if (monthData && monthData.event_list) {
        // Each key in event_list is a project (game_title), value is array of events for that project
        const consolidatedEvents: any[] = [];
        Object.values(monthData.event_list).forEach((projectEvents: any) => {
          if (Array.isArray(projectEvents) && projectEvents.length > 0) {
            // Filter checked events
            const checkedEvents = projectEvents.filter(
              (e: any) => e.is_checked === 1,
            );
            if (checkedEvents.length > 0) {
              // Take first event as base (they share game_title, studio_name, cover, date)
              const baseEvent = checkedEvents[0];
              // Collect all event_content from this project
              const eventContents = checkedEvents.map((e: any) => e.event_content);
              consolidatedEvents.push({
                ...baseEvent,
                event_contents: eventContents, // Array of event contents
              });
            }
          }
        });

        if (consolidatedEvents.length > 0) {
          timelineEvents[month] = consolidatedEvents;
        }
      }
    });

    // Preserve showEventBubbles state when updating bubbleEvent
    // This ensures user's toggle choice is not reset when switching cards
    setChartEventSetting((prev) => ({
      ...prev,
      bubbleEvent: timelineEvents,
    }));

    // After updating event data, wait for chart to re-render then show events
    // Polling check to ensure 'TopTipsEvent' series is present and coordinates are valid
    let retryCount = 0;
    const checkChartReady = () => {
      retryCount++;
      // Fallback: If validation fails for 3 seconds (60 * 50ms), show anyway
      if (retryCount > 60) {
        setIsChartReady(true);
        return;
      }

      if (!chartRef.current) {
        setTimeout(checkChartReady, 50);
        return;
      }

      const hasEvents = Object.keys(timelineEvents).length > 0;
      // If no events to show, we don't need to wait for the specific series
      if (!hasEvents) {
        setIsChartReady(true);
        return;
      }

      const chartOption = chartRef.current.getOption();
      const hasTopTipsEvent = chartOption?.series?.some(
        (s: any) => s.name === 'TopTipsEvent',
      );

      if (hasTopTipsEvent) {
        const xAxisData = chartOption?.xAxis?.[0]?.data || [];
        const testXAxis = xAxisData.find((x: string) => x && x.length > 0);

        if (testXAxis) {
          try {
            const testPosition = chartRef.current.convertToPixel(
              { seriesName: 'TopTipsEvent' },
              [testXAxis, 0],
            );
            // Verify we got a valid number (coordinate system is ready)
            if (testPosition && !isNaN(testPosition[0])) {
              setIsChartReady(true);
              return;
            }
          } catch (e) {
            // chart instance might be busy or invalid state
          }
        }
      }

      // Retry if series not found or pixels invalid
      setTimeout(checkChartReady, 50);
    };

    checkChartReady();
  }, [chartRef, eventApiData]);

  // Reset chart ready state when chart type or active card changes
  useEffect(() => {
    setIsChartReady(false);
  }, [showChatsTypeValue, activeCard, chartEventSetting.showEventBubbles]);

  // Handle window resize - reposition events when chart resizes
  // Following Opinion module pattern: hide events during resize, show after chart finishes resizing
  useEffect(() => {
    const handleResize = debounce(() => {
      // Hide events immediately when resize starts
      setIsChartReady(false);

      if (chartRef.current) {
        // Resize the chart to fit new container size
        chartRef.current.resize();

        // After chart resizes, wait for it to finish rendering then show events
        setTimeout(() => {
          setIsChartReady(true);
        }, 100);
      }
    }, 500); // Use 500ms debounce like Opinion module

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // Cancel pending debounced calls
    };
  }, [chartRef]);

  return {
    chartEventSetting,
    setChartEventSetting,
    eventSettingVisible,
    setEventSettingVisible,
    isChartReady,
    setIsChartReady,
    eventDataByGame,
    showEventsDisabled,
  };
};

export default useEventSetting;
